import React, {useCallback, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import moment from 'moment';
import _ from 'lodash';
import config from '../../../../app/config';
import makeStyles from '@mui/styles/makeStyles';
import {Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip} from '@mui/material';
import FemaleIcon from '@mui/icons-material/Female';
import MaleIcon from '@mui/icons-material/Male';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {
    BMData,
    BMPanelType,
    BMWardBedsHydrated,
    getWard as getWardObject,
    openModal,
    setBedAllocateChosenPatient,
} from '../../../../store/reducers/bedManagement';
import {getTableScrollbar} from '../../../../util/scrollbar';
import BedManagementTimer from './bedManagementTimer';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import Typography from '@mui/material/Typography';
import {AgyleActionIcon, AgyleNamedIcon, isIconNameValid} from '../../../../components/icons/agyle';
import {getBedAllocationStatus, getBedEquipmentIcons} from '../../bmUtils';
import {TimerCountUp} from '../../../../controls/agyle/timer';
import {UtilsMomentJS} from '../../../../store/utilsMoment';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {mapHospitalList} from '../../../../store/reducers/ward';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import IconButton from '@mui/material/IconButton';
import {calculateArrivalDate} from '../../../../util/patientUtil';
import {useEventListener} from '../../../../hooks/useEventListener';
import {setSnackbar} from '../../../../store/reducers/globals';

export const useTableStyles = makeStyles((theme) => ({
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
    table: {
        zIndex: 2,
        position: 'relative',
        borderCollapse: 'separate',
        borderSpacing: '0 8px',
    },
    header: {
        position: 'sticky',
        backgroundColor: theme.palette.mode === 'light' ? '#eff1f3' : '#3d3e40',
        zIndex: 4,
    },
    headCell: {
        fontSize: 13,
        backgroundColor: theme.palette.mode === 'light' ? '#eff1f3' : '#3d3e40',
        color: theme.palette.mode === 'light' ? '#000' : '#ACAFB3',
        position: 'sticky',
        padding: '8px 10px',
        zIndex: 1,
        borderBottom: 'none',
        whiteSpace: 'nowrap',
        '&:first-child': {
            borderTopLeftRadius: 4,
            borderBottomLeftRadius: 4,
        },
        '&:last-child': {
            borderTopRightRadius: 4,
            borderBottomRightRadius: 4,
        },
    },
    sticky: {
        zIndex: 4,
        position: 'sticky',
        '&.border': {
            borderRight: '2px solid #5d5f61',
        },
    },
    stickyRow: {
        zIndex: 3,
        position: 'sticky',
    },
    rowCell: {
        fontSize: 'small',
        backgroundColor: theme.palette.mode === 'light' ? '#f0f0f0' : '#333333',
        borderBottom: 'none',
        padding: 8,
        minHeight: 50,
        whiteSpace: 'nowrap',
        '&:first-child': {
            borderTopLeftRadius: 4,
            borderBottomLeftRadius: 4,
        },
        '&:last-child': {
            borderTopRightRadius: 4,
            borderBottomRightRadius: 4,
        },
    },
    row: {
        backgroundColor: theme.palette.mode === 'light' ? '#f0f0f0' : '#333333',
    },
    // Table component style
    ack: {
        width: 24,
        height: 24,
        backgroundColor: theme.palette.mode === 'light' ? theme.palette.background.paper : '#FFFFFF',
        color: theme.palette.mode === 'light' ? '#000' : theme.palette.primary.main,
        fontSize: 16,
        fontWeight: 'bold',
        borderRadius: 5,
        marginRight: 10,
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: '4px 6px',
    },
    status: {
        padding: '4px 6px',
        minWidth: 75,
        textAlign: 'center',
        borderRadius: 6,
        fontWeight: 700,
        color: '#ffffff',
        fontSize: 12,
        '&.allocated': {
            backgroundColor: '#054595',
        },
        '&.cancelled, &.canceled, &.unused': {
            backgroundColor: '#e25b61',
        },
        '&.completed, &.ready, &.open': {
            backgroundColor: '#4eac6f',
        },
        '&.requested': {
            backgroundColor: '#e25b61',
        },
        '&.closed, &.pre-reg, &.nr': {
            backgroundColor: theme.palette.designSystem.palette.light1,
            color: theme.palette.mode === 'light' ? '#FFFFFF' : '#ACAFB3',
        },
        '&.cleaning, &.occupied, &.blocked': {
            backgroundColor: '#F7A202',
        },
        '&.available': {
            backgroundColor: '#4EAC6F',
        },
        '&.siderooms': {
            border: `1px solid ${theme.palette.mode === 'light' ? '#000' : '#ffffff'}`,
            minWidth: 100,
            fontWeight: 500,
            color: theme.palette.designSystem.palette.text1,
        },
        '&.home, &.reserved, &.disabled': {
            backgroundColor: '#ACAFB3',
            color: '#313233',
        },
    },
    ptName: {
        textTransform: 'uppercase',
        fontWeight: 'bold',
    },
    gender: {
        marginRight: 8,
        borderRadius: 6,
        color: '#222425',
        display: 'flex',
        padding: 2,
        '&.Male': {
            backgroundColor: '#92bfe2',
        },
        '&.Female': {
            backgroundColor: '#f4bdd1',
        },
    },
    age: {
        color: '#ACAFB3',
        fontSize: 12,
    },
    spaceBetween: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    patientType: {
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        width: '70px',
        borderRadius: 5,
        height: '25px',
        lineHeight: '14px',
        fontWeight: 700,
        border: '1px solid transparent',
        paddingTop: 1,

        '&.Inpatient': {
            borderColor: '#4981B3',
            color: '#4981B3',
        },
        '&.TCI': {
            borderColor: '#B75BE2',
            color: '#B75BE2',
        },
        '&.ED': {
            borderColor: '#359F9F',
            color: '#359F9F',
        },
    },
    obsScore: {
        width: '30px',
        height: '30px',
        display: 'flex',
        paddingTop: 1,
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 8,
        fontWeight: 700,
        fontSize: '0.8rem',
        color: '#ffffff',
        '&.green': {
            backgroundColor: '#4EAC6F',
        },
        '&.red': {
            backgroundColor: '#E25B61',
        },
        '&.yellow': {
            backgroundColor: '#F7A202',
        },
        '&.grey': {
            backgroundColor: '#ACAFB3',
        },
    },
    speciality: {
        height: '25px',
        display: 'flex',
        flexBasis: 'content',
        paddingLeft: 5,
        paddingRight: 5,
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 5,
        paddingTop: 1,
        fontSize: '0.8rem',
        color: theme.palette.mode === 'light' ? '#000' : '#ffffff',
        border: `1px solid #ACAFB3`,
    },
    timer: {
        fontWeight: 700,
        fontSize: '1rem',
    },
    statusBtn: {
        minWidth: 160,
        borderColor: '#ACAFB3',
        color: '#ACAFB3',
        '&.patient': {
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            minWidth: 182,
        },
        '&.ready': {
            borderColor: '#4EAC6F',
            color: '#4EAC6F',
        },
    },
    fullRow: {
        display: 'flex',
        flexDirection: 'column',
    },
    withAction: {
        cursor: 'pointer',
    },
    timerDynamic: {
        '& .timer-count-up': {
            minWidth: 100,
            userSelect: 'none',
            padding: '5px 10px',
            borderRadius: 5,
            fontWeight: 'bold',
            fontSize: '120%',
            height: 30,
            textAlign: 'center',
        },
    },
    button: {
        transform: `scale(${20 / 24})`,
        borderColor: '#ACAFB3',
        color: '#ACAFB3',
        maxWidth: 160,
        '&.done': {
            borderColor: '#4EAC6F',
            color: '#4EAC6F',
        },
    },
    admitReason: {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        paddingRight: 15,
        cursor: 'pointer',
    },
    noteTooltip: {
        cursor: 'pointer',
        marginLeft: 10,
    },
    groupedRow: {
        fontWeight: 'bold',
        textAlign: 'center',
    },
    cleaningLevel: {
        width: 30,
        height: 30,
        display: 'flex',
        paddingTop: 1,
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 8,
        fontWeight: 700,
        fontSize: '0.8rem',
        color: '#ffffff',
        '&.level1': {
            backgroundColor: '#4EAC6F',
        },
        '&.level2': {
            backgroundColor: '#F7A202',
        },
        '&.level3': {
            backgroundColor: '#E25B61',
        },
    },
    locationName: {
        width: '33%',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
    },
}));

export const ptTypeMap = {
    TCI: 'TCI',
    I: 'Inpatient',
    IP: 'Inpatient',
    E: 'ED',
};

const STICKY_IF_BIGGER_THAN = 1024;

const BedManagementTable = (props) => {
    const classes = useTableStyles();
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const panelType = useSelector(BMPanelType);
    const hydratedBeds = useSelector(BMWardBedsHydrated);
    const bmData = useSelector(BMData);
    const hospitalList = useSelector(mapHospitalList);

    const stickyCellsRefs = useRef([]);

    useEffect(() => {
        calculateLeftValues();
    });

    useEventListener('resize', calculateLeftValues);

    function calculateLeftValues() {
        let leftValue = 0;
        stickyCellsRefs.current.forEach((cellRef) => {
            if (cellRef) {
                const rows = document.getElementsByName('row-' + cellRef.id);

                cellRef.style.left = `${leftValue}px`;
                _.forEach(rows, t => {
                    t.style.left = `${leftValue}px`;
                });
                leftValue += cellRef.offsetWidth;
            }
        });
    }

    const checkStatus = useCallback((currentPatientInWard) => {
        let dischargeDate;
        if (currentPatientInWard?.studies?.discharge) {
            const date = moment(currentPatientInWard?.studies?.discharge?.departureDate, config.ecdsDateFormat);
            const time = moment(currentPatientInWard?.studies?.discharge?.departureTime).set(
                {
                    year: date.get('year'),
                    month: date.get('month'),
                    day: date.get('day'),
                },
            ).toISOString();
            if (!moment(currentPatientInWard?.studies?.discharge?.departureTime).isValid()) {
                dischargeDate = UtilsMomentJS.getLocalDateFromUTCECDSFormat(currentPatientInWard?.studies?.discharge?.departureDate, currentPatientInWard?.studies?.discharge?.departureTime);
            } else {
                dischargeDate = time;
            }
        }
        return dischargeDate;
    }, [moment().seconds()]);

    const getTableHeight = () => {
        let height = document.getElementById('bm-panels')?.clientHeight;
        if (props.paging) height += 10;
        return `calc(100vh - ${height + 50}px)`;
    };

    const onBedStatusClick = (rowData) => {
        if (!!props.onBedStatusClick) {
            props.onBedStatusClick(rowData);
        }
    };

    const onPatientStatusClick = (rowData) => {
        if (!!props.onPtReadyBtnClick) {
            props.onPtReadyBtnClick(rowData);
        }
    };

    const getData = (type, val, rowData) => {
        const isPatientFromED = rowData?.admission?.attendanceType === 'E' || rowData?.patient?.admission?.attendanceType === 'E';

        switch (type) {
            case 'string':
            case 'stringWithPatient':
                return getStringVal(type, val, rowData);
            case 'startCase':
                return _.startCase(val);
            case 'bedType':
                return getUppercaseString(val, rowData);
            case 'openIn':
                return getOpenIn(val, rowData);
            case 'date':
                return val ? moment(val).format(config.defaultDateTimeFormat) : '-';
            case 'bool':
            case 'boolWithPatient':
            case 'boolWithNote':
                return getBoolean(type, val, rowData);
            case 'statusWithAck':
            case 'status':
                return getStatus(checkBedRequestFinalStatus(rowData), type === 'statusWithAck', type, rowData);
            case 'bedStatus':
                // return getBedStatus(checkBedRequestFinalStatus(rowData?.singleBedRequest || rowData?.patient?.singleBedRequest), type === 'statusWithAck', type, rowData);
                return getPatientStatus(type, rowData);
            case 'bedStatusBedCtx':
                return getBedContextStatusData(type, rowData);
            case 'ptReadyStatus':
                return getPtReadyStatus(val, rowData);
            case 'ackStatus':
                return getAcknowledgedStatus(val, rowData);
            case 'edd':
            case 'cdd':
                return getDischargeDate(type, val, rowData);
            case 'diagnosis':
                return getPatientDiagnosis(rowData, isPatientFromED);
            case 'patientTimeInED':
                return getPatientInEDTime(rowData, isPatientFromED);
            case 'bed':
            case 'bedWithZone':
                return getBed(val, rowData);
            case 'destinationBed':
                return getDestinationBed(val, rowData);
            case 'bedBedTable':
                return getBedTableBed(val, rowData);
            case 'timer':
            case 'timerBed':
                return getTimer(type, val, rowData);
            case 'waitReqToAlloc':
            case 'waitReqToRdy':
            case 'waitRdyToComp':
                return getBedWaitTimer(type, val, rowData);
            case 'care':
                return getCare(val, rowData);
            case 'equipment':
                return getEquipment(val, rowData);
            case 'obs':
            case 'obsBed':
                return getObs(type, val, rowData);
            case 'bedWait':
                return getBedWait(val, rowData);
            // case 'speciality':
            case 'spec':
            case 'specBedCtx':
                return getSpeciality(type, val, rowData);
            case 'ptDetails':
            case 'ptDetailsBed':
                return getPtDetails(type, val);
            // case 'ptDetails':
            //     return getPtDetailsBed(val, rowData);
            case 'ptNumbers':
            case 'ptNumbersBed':
                return getPtNumbers(type, val, rowData);
            case 'ptType':
            case 'ptTypeBed':
                return getPatientType(type, val, rowData);
            case 'location':
                return getBedLocation(type, val, rowData);
            case 'wardObj':
                return getWard(val, rowData);
            case 'statuses':
                return getStatuses(val, rowData);
            case 'wholePatient':
            case 'wholePatientOccupied':
                return getWholePatientData(type, val, rowData, type === 'wholePatient');
            case 'admitLocation':
                return getAdmitLocation(val, rowData);
            case 'ptTransport':
                return getPtTransport(val, rowData);
            case 'chiefComplaint':
                return getChiefComplaint(val, rowData);
            case 'arrivalTime':
                return getArrivalTime(val, rowData);
            case 'actionIcon':
                return getActionIcon(type, val, rowData);
            case 'sideroomReason':
                return getSideroomReason(type, val, rowData);
            case 'infControl':
            case 'infControlBedCtx':
                return getInfectionControl(type, val, rowData);
            case 'admitReason':
                return getAdmitReason(val, rowData);
            case 'leaveType': {
                if (!val) return '-';
                else if (val?.offWard) return 'Off Ward';
                else if (val?.homeLeave) return 'Home Leave';
                break;
            }
            case 'hospitalId': {
                const hospital = _.find(hospitalList, {__id: val});
                return hospital?.description || '-';
            }
            case 'cleaningLevel':
                return getCleaningLevel(val);
            case 'acknowledge':
                return getAcknowledge(rowData);
            case 'actionCleaning':
                return getActionCleaning(rowData);
            default:
                return Math.random();
        }
    };

    /**
     *  Table Row component
     */

    const getEmptyCell = () => {
        return <div className={classes.divRow}/>;
    };

    const getStringVal = (type, value, rowData) => {
        if (type === 'stringWithPatient' && !value) return getEmptyCell();
        return value || '-';
    };

    const getBoolean = (type, value, rowData) => {
        if (type === 'boolWithPatient' && !value) return getEmptyCell();
        if (type === 'boolWithNote' && value) return getBoolWithNote(type, value, rowData);
        return !!value ? t('Yes') : value === null ? t('N/R') : t('No');
    };

    const getBoolWithNote = (type, value, rowData) => {
        const isNote = !!value?.note;

        return (
            <div className={clsx(classes.divRow)}>
                {getBoolean('bool', value?.checked)}

                {isNote && (
                    <Tooltip title={value.note} sx={{pt: .5}}>
                        <div className={classes.noteTooltip}>
                            <AgyleNamedIcon name="info"/>
                        </div>
                    </Tooltip>
                )}
            </div>
        );
    };

    const getPatientSpeciality = (bedRequest) => {
        if (!bedRequest?.specialty) {
            return '-';
        }

        const {__description: description, __code: code} = bedRequest?.specialty;

        return (
            <Tooltip title={description}>
                <div className={classes.speciality}>
                    {code}
                </div>
            </Tooltip>
        );
    };

    const getWardSpecialty = (value, rowData) => {
        const ward = dispatch(getWardObject(rowData?.wardId));
        let wardSpec;
        if (ward && !!ward?.specialtyRestriction && !_.isEmpty(ward.specialtyRestriction[rowData.wardId])) {
            wardSpec = ward.specialtyRestriction[rowData.wardId][0];
        }
        return (wardSpec &&
            <div className={clsx(classes.speciality)}>
                {wardSpec?.__code}
            </div>
        );
    };

    const getSpeciality = (type, value, rowData) => {
        if (type === 'specBedCtx') {
            return getWardSpecialty(value, rowData);
        } else {
            return getPatientSpeciality(value, rowData);
        }
    };

    const getUppercaseString = (value) => {
        return value ? _.capitalize(value) : '-';
    };

    const getCare = (value, rowData) => {
        if (panelType === 'siderooms' && !rowData?.sideroom) {
            return getEmptyCell(rowData);
        }

        return value ? `${_.capitalize(value)} Care` : '-';
    };

    const calculateBedWait = (value) => moment().diff(moment(value), 'minutes');

    const getBedWait = (value, rowData) => {
        if (panelType === 'siderooms' && !rowData?.sideroom) {
            return getEmptyCell(rowData);
        }
        const requestTime = value?.requested?.when;

        const getBedWaitValue = () => {
            const bedWait = calculateBedWait(requestTime);
            return bedWait > 0 ? bedWait : 0;
        };

        const getBedWaitColor = () => {
            const bedWait = calculateBedWait(requestTime);
            if (bedWait > 0 && bedWait <= 30) {
                return 'green';
            } else if (bedWait > 30 && bedWait <= 60) {
                return 'yellow';
            } else if (bedWait > 60) {
                return 'red';
            } else {
                return 'grey';
            }
        };

        return (
            <div className={clsx(classes.divRow, classes.timer, getBedWaitColor())}>
                {moment().subtract(getBedWaitValue()).format('HH:MM:ss')}
            </div>
        );
    };

    const getObs = (type, value, rowData) => {
        const field = type === 'obs' ? value : rowData?.patient?.patient;

        if (!field) {
            return getEmptyCell(rowData);
        }

        const obsScore = _.isNumber(field?.lastObservation?.totalScore) ? field?.lastObservation?.totalScore : '-';

        const obsBackground = () => {
            if (obsScore >= 0 && obsScore <= 4) {
                return 'green';
            } else if (obsScore >= 5 && obsScore <= 6) {
                return 'yellow';
            } else if (obsScore >= 7) {
                return 'red';
            } else {
                return 'grey';
            }
        };
        return (
            <div className={clsx(classes.divRow, classes.obsScore, obsBackground())}>
                {obsScore}
            </div>
        );
    };

    const getBed = (value, rowData) => {
        const {areaDescription, bed, bedDescription} = rowData;

        if (panelType === 'siderooms' && !rowData?.sideroom) {
            return getEmptyCell(rowData);
        }

        let text = `${areaDescription || '-'} / ${bedDescription || '-'}`;
        if (!bed) text = 'Holding Zone';
        if (rowData?.admission?.attendanceType === 'TCI') text = '-';

        return (
            <div className={classes.divRow}>
                {text}
            </div>
        );
    };

    const getDestinationBed = (value) => {
        if (!value) {
            return (
                <div className={classes.divRow}>
                    - / -
                </div>
            );
        }

        const {bayName, bedName} = value;
        let text = `${bayName || '-'} / ${bedName || '-'}`;

        return (
            <div className={classes.divRow}>
                {text}
            </div>
        );
    };

    const getBedTableBed = (value, rowData) => {
        const bayName = rowData?.bay?.description;
        const bedName = rowData?.name;

        let text = `${bayName || '-'} / ${bedName || '-'}`;

        return (
            <div className={classes.divRow}>
                {text}
            </div>
        );
    };

    const getBedLocation = (type, value, rowData) => {
        const {name, bay, ward} = rowData;

        return (
            <div className={classes.divRow} style={{justifyContent: 'space-between', paddingRight: 10}}>
                <span className={classes.locationName} style={{fontWeight: 700}}>
                    {name}
                </span>

                <span className={classes.locationName}>
                    {bay?.description}
                </span>

                <span className={classes.locationName}>
                    {ward?.description || ward?.name}
                </span>
            </div>
        );
    };

    const getWard = (value, rowData) => {
        const {ward} = rowData;
        return (
            <div className={classes.divRow} style={{justifyContent: 'space-between', paddingRight: 10}}>
                <span>{ward?.description}</span>
            </div>
        );
    };

    const getPatientType = (dataType, value, rowData) => {
        const field = dataType === 'ptType' ? value : rowData?.patient?.admission;
        const pyType = ptTypeMap[field?.attendanceType];

        return (
            <div className={clsx(classes.patientType, pyType)}>{pyType}</div>
        );
    };

    const checkBedRequestFinalStatus = (rowData) => {
        const bedRequest = rowData?.singleBedRequest || rowData?.patient?.singleBedRequest;

        // If completed or canceled then don't check bed status
        if (bedRequest?.status !== 'NEW') return t('Completed');
        if (bedRequest?.canceled?.checked) return t('Cancelled');

        return rowData?.bedReadyStatus || 'Requested';
    };

    const getSideroomStatus = (rowData) => {
        const {name} = rowData;

        return (
            <div className={classes.divRow}>
                <div
                    className={clsx(
                        classes.status,
                        panelType,
                    )}>{name || 'status'}</div>
            </div>
        );
    };

    const getOffwardStatus = (rowData) => {
        const status = rowData?.patient ? checkBedRequestFinalStatus(rowData) : rowData?.available ? 'Available' : rowData?.archived ? 'Closed' : 'Pre-Reg';

        return (
            <div className={classes.divRow}>
                <div
                    className={clsx(
                        classes.status,
                        status?.toLowerCase(),
                    )}>
                    {status || 'status'}
                </div>
            </div>
        );
    };

    const findPatientBed = (rowData) => {
        return _.find(hydratedBeds, {__id: rowData?.bed});
    };

    const getPatientStatus = (type, rowData) => {
        let bed = type === 'bedStatus' ? findPatientBed(rowData) : rowData;

        const statusText = getBedAllocationStatus(bed, bmData);
        return (
            <div className={classes.divRow}>
                <div
                    className={clsx(
                        classes.status,
                        statusText.toLowerCase(),
                    )}>
                    {statusText || 'status'}
                </div>
            </div>
        );
    };

    const getBedContextStatusData = (type, rowData) => {
        const status = rowData?.status;
        return (
            <div className={classes.divRow}>
                <div
                    className={clsx(
                        classes.status,
                        status?.toLowerCase(),
                    )}>
                    {status || 'status'}
                </div>
            </div>
        );
    };

    const getPtReadyStatus = (bedRequest, rowData) => {
        const isPtTCI = rowData?.admission.attendanceType === 'TCI';
        const ptStatus = isPtTCI ? 'Ready' : bedRequest?.patientReady?.checked ? 'Ready' : 'Not Ready';

        return (
            <div className={classes.divRow}>
                <div
                    className={clsx(
                        classes.status,
                        _.camelCase(ptStatus === 'Not Ready' ? 'nr' : ptStatus),
                    )}
                >
                    {ptStatus}
                </div>
            </div>
        );
    };

    const getAcknowledgedStatus = (bedReq) => {
        const isAck = bedReq?.requestedAcknowledge?.checked;

        return (
            <div className={classes.divRow} style={{marginLeft: -14}}>
                <AgyleButton
                    disabled
                    color="primary"
                    variant="outlined"
                    startIcon={<CheckCircleOutlineIcon/>}
                    className={clsx(classes.button, 'acknowledge', isAck && 'done')}
                >
                    {t(isAck ? 'Acknowledged' : 'Not acknowledged')}
                </AgyleButton>
            </div>
        );
    };

    const getStatus = (value, withAck, type, rowData) => {
        if (panelType === 'siderooms') return getSideroomStatus(rowData);
        if (panelType === 'offWard') return getOffwardStatus(rowData);
        return (
            <div className={classes.divRow}>
                {withAck &&
                    <div
                        className={classes.ack}
                        onClick={() => props?.onStatusIconClick(rowData)}
                    >
                        !
                    </div>
                }
                <div
                    className={clsx(
                        classes.status,
                        value && value?.toLowerCase(),
                        panelType,
                    )}
                >
                    {value || 'status'}
                </div>
            </div>
        );
    };

    const getGender = (gender) => {
        let icon;
        let color;

        switch (gender) {
            case 'Female':
                icon = <FemaleIcon fontSize="small"/>;
                color = '#F4BDD1';
                break;
            case 'Male':
                icon = <MaleIcon fontSize="small"/>;
                color = '#92BFE2';
                break;
            default:
                icon = <QuestionMarkIcon fontSize="small"/>;
                color = '#acafb3';
        }

        return (
            <div className={classes.gender} style={{backgroundColor: color}}>
                {icon}
            </div>
        );
    };

    const getPtDetails = (dataType, pt) => {
        const patient = dataType === 'ptDetails' ? pt : pt?.patient;

        if (!patient) {
            return <div className={classes.divRow} style={{fontSize: '.7rem', fontStyle: 'italic'}}>
                No patient yet allocated
            </div>;
        }
        const age = patient?.dob ? moment().diff(patient?.dob, 'years') : 'UNK';

        return (
            <div className={classes.fullRow}>
                <div>
                    <span className={classes.ptName}>{patient?.familyName},</span> {patient?.firstName}
                </div>

                <div className={classes.divRow}>
                    {getGender(patient?.gender)}
                    <span className={classes.age}>Age: {age}</span>
                </div>
            </div>
        );
    };

    const handleCopyPtNumber = (number) => {
        if (!navigator.clipboard || !number) return;
        navigator.clipboard.writeText(number)
            .then(() => {
                dispatch(setSnackbar({
                    type: 'success',
                    message: 'The patient number has been copied to the clipboard.',
                }));
            });
    };

    const getPtNumbers = (type, pt, rowData) => {
        const field = type === 'ptNumbersBed' ? pt?.patient : pt;

        if (!field) return getEmptyCell();

        return (
            <div className={classes.fullRow}>
                <div className={classes.spaceBetween} style={{minWidth: 165}}>
                    <span className={classes.age} style={{minWidth: 50}}>Hos. No:</span>
                    <span style={{cursor: 'pointer'}} onClick={handleCopyPtNumber.bind(this, field?.hospitalNumber)}>{field?.hospitalNumber || '-'}</span>
                </div>

                <div className={classes.spaceBetween} style={{minWidth: 165}}>
                    <span className={classes.age} style={{minWidth: 50}}>NHS No:</span>
                    <span  style={{cursor: 'pointer'}} onClick={handleCopyPtNumber.bind(this, field?.nhsNumber)}>{field?.nhsNumber || '-'}</span>
                </div>
            </div>
        );
    };

    const getTimer = (dataType, value, rowData) => {
        const field = dataType === 'timer' ? value : rowData?.patient?.singleBedRequest?.requested?.when;
        return (
            <div className={classes.timer}>
                <BedManagementTimer
                    time={field}
                />
            </div>
        );
    };

    const getBedWaitTimer = (type, value) => {
        if (!value) return '-';
        let from = {when: null};
        let to = {when: null};
        const {allocated, requested, patientReady} = value;

        switch (type) {
            case 'waitReqToAlloc':
                from = requested;
                to = allocated;
                break;
            case 'waitReqToRdy':
                from = allocated;
                to = patientReady;
                break;
            default:
                break;
        }

        if (!from?.when && !to?.when) return '-';

        return (
            <div className={clsx(classes.divRow, classes.timer)}>
                <BedManagementTimer
                    time={moment(from?.when).toISOString()}
                    timeTo={moment(to?.when).toISOString()}
                />
            </div>
        );
    };

    const getEquipment = (value, rowData) => {
        if (_.isEmpty(value)) return 'None';

        return (
            <div className={classes.divRow}>
                {getBedEquipmentIcons(rowData)}
            </div>
        );
    };

    const getInfections = (value) => {
        if (!value) return null;
        const infections = [];

        const isDateValid = (date) => {
            return date && moment(date).isSameOrAfter(moment());
        };

        Object.entries(value).forEach(entry => {
            const [key, value] = entry;
            if (isDateValid(value?.expired)) {
                let iconName;
                if (key === 'covid') {
                    switch (value?.status) {
                        case 'confirm':
                            iconName = 'covidPositive';
                            break;
                        case 'none':
                            iconName = 'covidNegative';
                            break;
                        case 'suspected':
                        default:
                            iconName = 'covid';
                            break;
                    }
                } else {
                    const suffix = value?.state === 'suspected' ? 'S' : '';
                    iconName = key + suffix;
                }
                if (isIconNameValid(iconName)) infections.push({iconName, status: value?.status});
            }
        });

        return (
            <div className={classes.divRow} style={{gap: 5}}>
                {infections.map((inf) => {
                    const getColor = () => {
                        // eslint-disable-next-line default-case
                        switch (inf.status) {
                            case 'none':
                                return '#01AF69';
                            case 'confirm':
                                return '#DC4A5D';
                            case 'suspected':
                                return '#FEB03A';
                        }
                    };

                    return (
                        <AgyleNamedIcon
                            key={inf.iconName}
                            name={inf?.iconName}
                            style={{color: getColor()}}
                        />
                    );
                })}
            </div>
        );
    };

    const getStatuses = (value, rowData) => {
        const isBedReady = !rowData?.bedStatus;
        const isPatientReady = !!rowData?.patient?.singleBedRequest?.patientReady?.checked;

        return (
            <div className={classes.divRow} style={{justifyContent: 'flex-end'}}>
                <AgyleButton
                    variant="outlined"
                    onClick={() => onBedStatusClick(rowData)}
                    startIcon={isBedReady ? <CheckCircleOutlinedIcon/> : <CancelOutlinedIcon/>}
                    className={clsx(classes.statusBtn, 'bed', isBedReady && 'ready')}
                >
                    Bed is {isBedReady ? 'Ready' : 'Not Ready'}
                </AgyleButton>

                {rowData?.patient &&
                    <AgyleButton
                        variant="outlined"
                        // onClick={() => onPatientStatusClick(rowData)}
                        startIcon={isPatientReady ? <CheckCircleOutlinedIcon/> : null}
                        className={clsx(classes.statusBtn, 'patient', isPatientReady && 'ready')}
                        disabled
                    >
                        {isPatientReady ? 'Patient is Ready' : 'Patient is not Ready'}
                    </AgyleButton>
                }
            </div>
        );
    };

    const getOpenIn = (value, rowData) => {
        if (panelType === 'siderooms' && !rowData?.sideroom) {
            return getEmptyCell(rowData);
        }

        return (
            <div className={classes.divRow}>
                {value !== 'Unknown' ?
                    <BedManagementTimer
                        countDown
                        countDownTo={value}
                        time={value}
                    /> :
                    value
                }
            </div>
        );
    };

    const getWholePatientData = (type, value, rowData, allocatedPt) => {
        if (!value) {
            return <div className={classes.divRow} style={{fontSize: '.7rem', fontStyle: 'italic'}}>
                {t(allocatedPt ? 'No patient yet allocated' : 'Bed available')}
            </div>;
        }

        return (
            <div className={classes.divRow} style={{gap: 20, paddingLeft: 5}}>
                <div style={{minWidth: '150px'}}>
                    {getPtDetails('', value)}
                </div>

                {getPtNumbers('ptNumbersBed', value)}
                {getPatientType('ptType', value, rowData)}
                {allocatedPt && getTimer('timer', value, rowData)}
            </div>
        );
    };

    const getAdmitLocation = (value, rowData) => {
        return <div className={classes.divRow}>
            {rowData?.site}
        </div>;
    };

    const getPtTransport = (value, rowData) => {
        return <div className={classes.divRow}>
            {_.capitalize(value)}
        </div>;
    };

    const getChiefComplaint = (value, rowData) => {
        return <div className={classes.divRow}>
            {_.capitalize(value)}
        </div>;
    };

    const getArrivalTime = (value, rowData) => {

        return <div className={classes.divRow}>
            <Typography variant="body2" fontWeight={700}>
                {moment(value).format('hh:mm A')}
            </Typography>
        </div>;
    };

    const handleOpenRequestScreen = (rowData) => {
        const {patient} = rowData;
        dispatch(setBedAllocateChosenPatient({patient, request: patient?.singleBedRequest}));
        dispatch(openModal('requestScreen'));
    };

    const getActionIcon = (dataType, val, rowData) => {
        const isPatient = rowData?.patient;
        if (!isPatient) return '';

        return (
            <div className={classes.divRow} style={{cursor: 'pointer'}}
                 onClick={() => handleOpenRequestScreen(rowData)}>
                <AgyleNamedIcon name="actionIcon"/>
            </div>
        );
    };

    const getSideroomReason = (type, val, rowData) => {
        if (!rowData?.patient) {
            return getEmptyCell();
        }

        const field = type === 'sideroomReason' ? val : rowData?.sideroom;
        return (
            <div className={classes.divRow}>
                {field?.reason || '-'}
            </div>
        );
    };

    const getDischargeDate = (type, value, rowData) => {
        const val = !_.isNil(value) ? value : rowData;
        const isPtInpatient = val?.admission?.attendanceType === 'I' || val?.admission?.attendanceType === 'IP';
        if (!isPtInpatient) {
            return '-';
        }

        const validDate = val?.intendentManagement?.[type];
        return !validDate ? '-' : moment(validDate).format(config.defaultDateTimeFormat);
    };

    const getPatientDiagnosis = (rowData, isPtFromED) => {
        const diagnosisArr = rowData?.diagnosis?.primary || [];
        if (!isPtFromED || _.isEmpty(diagnosisArr)) {
            return '-';
        }

        return (
            <div className={classes.divRow} style={{flexDirection: 'column', alignItems: 'flex-start'}}>
                {diagnosisArr.map((diagnosis, index) => (
                    <div key={index + diagnosis.value?.snomedDescription}>
                        {diagnosis.value?.snomedDescription}
                    </div>
                ))}
            </div>
        );
        // }
    };

    const getPatientInEDTime = (currentPatientInWard, isPtFromED) => {
        if (!isPtFromED) {
            return '-';
        }

        const getPatientTime = (format) => {
            // If patient have arrival mode code for ambulance then calculate time form ambulance handover
            if (!_.isEmpty(props.arrivalMode) && props.arrivalMode.includes(currentPatientInWard.admission?.arrivalModeCode)) {
                return format ? moment(calculateArrivalDate(currentPatientInWard)).format(config.defaultDateTimeFormat) : calculateArrivalDate(currentPatientInWard);
            } else {
                return format ? moment(currentPatientInWard?.admission?.startDate).format(config.defaultDateTimeFormat) : moment(currentPatientInWard?.admission?.startDate);
            }
        };

        return (
            <div className={classes.divRow}>
                <div className={classes.timerDynamic}>
                    <TimerCountUp
                        renderWithBG
                        limit={240}
                        upperLimit={-1}
                        countDownSince={
                            currentPatientInWard.admission?.status === 'DISCHARGE_REQUESTED' ||
                            currentPatientInWard.admission?.status === 'TRANSFER_REQUESTED' ? checkStatus(currentPatientInWard) : false
                        }
                        time={getPatientTime()}
                        limitMessage={t('4hr Target Breached!')}
                    />
                </div>
            </div>
        );
    };

    const getInfectionControl = (type, val, rowData) => {
        let field;
        if (type === 'infControlBedCtx') {
            field = rowData.infectionControl;
        } else {
            const bed = findPatientBed(rowData);
            field = bed?.infectionControl;
        }
        if (!rowData) return getStringVal(val);

        return (
            <div className={classes.divRow}>
                {type === 'infControlBedCtx' ?
                    <div style={{color: field ? '#E25B61' : '#4EAC6F', justifyContent: 'center'}}>
                        <AgyleNamedIcon name={field ? 'covidPositive' : 'covidNegative'}/>
                    </div> :
                    getInfections(val)
                    // renderAlarmSection(
                    //     rowData,
                    //     referredToServiceObj,
                    //     null,
                    //     mainScreenClasses,
                    // )
                }
            </div>
        );
    };

    const checkIfNeedStickyCol = () => {
        const viewportWidth = window.innerWidth;
        return viewportWidth > STICKY_IF_BIGGER_THAN;
    };

    const getAdmitReason = (value) => {
        return (
            <div>
                <Tooltip
                    placement="top"
                    PopperProps={{disablePortal: true}}
                    title={value}
                >
                    <div className={classes.admitReason} style={{textAlign: 'center'}}>
                        {value || '-'}
                    </div>
                </Tooltip>
            </div>
        );
    };

    const getCleaningLevel = (value) => {
        return (
            <div>
                <div className={clsx(classes.cleaningLevel, `level${value}`)} style={{textAlign: 'center'}}>
                    {value || '-'}
                </div>
            </div>
        );
    };

    const getAcknowledge = (data) => {
        if (data?.acknowledge?.checked) {
            return (
                <>
                    <div>
                        {data?.acknowledge?.user}
                    </div>

                    <div>
                        {moment(data?.acknowledge?.when).format(config.defaultDateTimeFormat)}
                    </div>
                </>
            );
        } else {
            return t('N/A');
        }
    };

    const getActionCleaning = (data) => {
        let status = data?.status?.toLowerCase();
        return (
            <div className={classes.divRow} style={{gap: 5}}>
                <IconButton
                    onClick={() => props.onClick(data)}
                >
                    <AgyleActionIcon/>
                </IconButton>

                <div className={classes.divRow}>
                    <div
                        className={clsx(
                            classes.status,
                            status,
                            panelType,
                        )}
                    >
                        {_.capitalize(status) || 'status'}
                    </div>
                </div>
            </div>
        );
    };

    const renderGroupedTable = () => {
        const groupedObjects = props.data.reduce((grouped, record) => {
            if (!grouped[record[props.pagingBy]]) {
                grouped[record[props.pagingBy]] = [];
            }
            grouped[record[props.pagingBy]].push(record);
            return grouped;
        }, {});

        return (
            <TableBody>
                {Object.keys(groupedObjects).map((group) => (
                    <React.Fragment key={group}>
                        <TableRow className={classes.row}>
                            <TableCell
                                className={clsx(classes.rowCell, classes.groupedRow)}
                                colSpan={props.columns.length}
                                size="small"
                            >
                                {t('Ward')} - {group} - {groupedObjects[group]?.[0]?.wardId}
                            </TableCell>
                        </TableRow>
                        {groupedObjects[group].map((data) => (
                            <TableRow
                                key={Math.random()}
                                className={classes.row}
                            >
                                {props.columns.map((col, index) => {
                                    const isSticky = index <= props.stickColumns && checkIfNeedStickyCol();
                                    return (
                                        <TableCell
                                            id={`row-${col.id}`}
                                            name={`row-${col.id}`}
                                            key={col.id + Math.random()}
                                            className={clsx(
                                                classes.rowCell,
                                                isSticky && classes.stickyRow,
                                                props.onStatusIconClick && isSticky && col.type !== 'statusWithAck' && classes.withAction,
                                            )}
                                            style={{
                                                minWidth: col.width || 'auto',
                                                // maxWidth: (2 * col.width),
                                                maxWidth: (col.width + 70),
                                            }}
                                            onClick={() => col.type !== 'statusWithAck' && isSticky && (props.onClick && props.onClick(data))}
                                        >
                                            {getData(col.type, _.get(data, col.field), data)}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </React.Fragment>
                ))}
            </TableBody>
        );
    };

    return (
        <TableContainer
            style={{height: props.tableHeight ? props.tableHeight : getTableHeight()}}
            className={getTableScrollbar()}
        >
            <Table stickyHeader className={classes.table}>
                <TableHead>
                    <TableRow className={classes.header}>
                        {props.columns.map((col, index) => {
                            const isSticky = index <= props.stickColumns && checkIfNeedStickyCol();
                            return (
                                <TableCell
                                    key={col.id}
                                    id={col.id}
                                    className={clsx(
                                        classes.headCell,
                                        isSticky && classes.sticky,
                                        (isSticky && index === props.stickColumns) && 'border',
                                    )}
                                    padding="none"
                                    ref={(ref) => {
                                        if (isSticky) stickyCellsRefs.current[index] = ref;
                                    }}
                                    style={{minWidth: col.width || 'auto'}}
                                >
                                    {col.description}
                                </TableCell>
                            );
                        })}
                    </TableRow>
                </TableHead>

                {props.paging ? renderGroupedTable()
                    :
                    <TableBody>
                        {props.data.map((data) => {
                            return (
                                <TableRow
                                    key={Math.random()}
                                    className={classes.row}
                                >
                                    {props.columns.map((col, index) => {
                                        const isSticky = index <= props.stickColumns && checkIfNeedStickyCol();
                                        return (
                                            <TableCell
                                                id={`row-${col.id}`}
                                                name={`row-${col.id}`}
                                                key={col.id + Math.random()}
                                                className={clsx(
                                                    classes.rowCell,
                                                    isSticky && classes.stickyRow,
                                                    props.onStatusIconClick && isSticky && col.type !== 'statusWithAck' && classes.withAction,
                                                )}
                                                style={{
                                                    minWidth: col.width || 'auto',
                                                    // maxWidth: (2 * col.width),
                                                    maxWidth: (col.width + 70),
                                                }}
                                                onClick={() => col.type !== 'statusWithAck' && isSticky && (props.onClick && props.onClick(data))}
                                            >
                                                {getData(col.type, _.get(data, col.field), data)}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                }
            </Table>
        </TableContainer>
    );
};

export default BedManagementTable;

BedManagementTable.propTypes = {
    data: PropTypes.array.isRequired,
    columns: PropTypes.array.isRequired,
    stickColumns: PropTypes.number,
    onBedStatusClick: PropTypes.func,
    tableHeight: PropTypes.any,
    paging: PropTypes.bool,
    pagingBy: PropTypes.string,
};

BedManagementTable.defaultProps = {
    data: [],
    columns: [],
    stickColumns: null,
    onBedStatusClick: null,
    tableHeight: null,
    paging: false,
};